import PropTypes from "prop-types"
import classNames from "classnames"
import { useState } from "react"
import type { KeyboardEvent } from "react"
import { AutoAwesomeOutlined } from "@mui/icons-material"
import { Card, Chip, Typography } from "@mui/material"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { formatLastUpdated } from "connected-components/skills/utils/format"
import { SkillDownloadAction } from "connected-components/skills/components/skill-download-action"

import "./style.scss"

interface SkillCardProps {
  skill: SkillCatalogSkill
  canDownload?: boolean
}

function SkillCard({ skill, canDownload = false }: SkillCardProps) {
  const lastUpdated = formatLastUpdated(skill.lastUpdated)
  const versionLabel = skill.version ? `v${skill.version}` : null
  const metaParts = [versionLabel, lastUpdated && `Updated ${lastUpdated}`].filter(Boolean)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)

  function toggleDescription() {
    setDescriptionExpanded(current => !current)
  }

  function handleDescriptionKeyDown(event: KeyboardEvent<HTMLParagraphElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      toggleDescription()
    }
  }

  return (
    <Card square className="skill-card">
      <div className="skill-card-layout">
        <div className="skill-card-icon">
          <AutoAwesomeOutlined fontSize="medium" />
        </div>

        <div className="skill-card-content">
          <div className="skill-card-heading">
            <Typography variant="h6" component="h3" className="skill-card-title">
              {skill.name}
            </Typography>
            {skill.beta && (
              <Chip size="small" color="info" variant="outlined" label="Preview" className="skill-card-beta" />
            )}
          </div>

          <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            className={classNames("skill-card-description", {
              "skill-card-description--expanded": descriptionExpanded,
            })}
            role="button"
            tabIndex={0}
            aria-expanded={descriptionExpanded}
            onClick={toggleDescription}
            onKeyDown={handleDescriptionKeyDown}
          >
            {skill.description}
          </Typography>

          {metaParts.length > 0 && (
            <Typography variant="caption" color="textSecondary" className="skill-card-meta">
              {metaParts.join(" · ")}
            </Typography>
          )}
        </div>

        {canDownload && (
          <div className="skill-card-actions">
            <SkillDownloadAction skill={skill} noun="workflow" />
          </div>
        )}
      </div>
    </Card>
  )
}

SkillCard.propTypes = {
  skill: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    tier: PropTypes.oneOf(["licensed", "proprietary"]).isRequired,
    plugin: PropTypes.string,
    author: PropTypes.string,
    downloadFilename: PropTypes.string,
    lastUpdated: PropTypes.string,
    beta: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  canDownload: PropTypes.bool,
}

export default SkillCard
